/**
 * RPC URL Configuration Utilities
 * 
 * These utilities ensure consistent RPC URL construction across the application
 * and prevent port mismatch issues by always using the configured API URL
 * rather than relying on window.location.origin which can be unreliable.
 */

/**
 * Get the RPC proxy URL for client-side Solana connections
 * 
 * Priority order:
 * 1. NEXT_PUBLIC_API_URL environment variable (most reliable)
 * 2. Hardcoded localhost:3999 (guaranteed correct for development)
 * 3. Constructed URL from window.location (only in production/deployed)
 * 
 * @returns The complete RPC proxy URL
 */
export function getRpcProxyUrl(): string {
	if (typeof window !== "undefined") {
		// Client-side: Use configured API URL or default to localhost:3999
		const apiUrl = process.env.NEXT_PUBLIC_API_URL;
		
		if (apiUrl) {
			// Use the configured API URL (most reliable)
			const rpcUrl = apiUrl.replace('/api', '/api/rpc-proxy');
			return rpcUrl;
		}
		
		// Check if we're in local development (localhost)
		const hostname = window.location.hostname;
		if (hostname === 'localhost' || hostname === '127.0.0.1') {
			// ALWAYS use port 3999 for localhost to prevent port mismatch
			const rpcUrl = `http://localhost:3999/api/rpc-proxy`;
			
			if (process.env.NEXT_PUBLIC_ENV === "local") {
				console.warn('[RPC URL] NEXT_PUBLIC_API_URL not found, using hardcoded localhost:3999');
				console.warn('[RPC URL] Current window port:', window.location.port || 'default');
				console.warn('[RPC URL] If you see errors, navigate to http://localhost:3999');
			}
			
			return rpcUrl;
		}
		
		// Production fallback: Use window.location (should be correct in production)
		const protocol = window.location.protocol;
		const port = window.location.port;
		const portSuffix = port ? `:${port}` : '';
		const rpcUrl = `${protocol}//${hostname}${portSuffix}/api/rpc-proxy`;
		return rpcUrl;
	}
	
	// Server-side: Use environment variable or default
	return process.env.NEXT_PUBLIC_API_URL?.replace('/api', '/api/rpc-proxy') || 
		'http://localhost:3999/api/rpc-proxy';
}

/**
 * Get a WebSocket RPC endpoint for Solana subscriptions.
 *
 * IMPORTANT:
 * - Do NOT use the local `/api/rpc-proxy` URL for wsEndpoint.
 * - web3.js auto-derives ws:// from the HTTP endpoint and for localhost it
 *   becomes `ws(s)://localhost:3001/...`, which is not our RPC server.
 */
export function getRpcWebSocketUrl(): string {
	const directHttpRpc =
		process.env.NEXT_PUBLIC_ENV === "local"
			? process.env.NEXT_PUBLIC_DEVELOPMENT_RPC ||
			  process.env.NEXT_PUBLIC_SOLANA_RPC_URL
			: process.env.NEXT_PUBLIC_PRODUCTION_RPC ||
			  process.env.NEXT_PUBLIC_SOLANA_RPC_URL;

	if (directHttpRpc) {
		return directHttpRpc.replace(/^http/i, "ws");
	}

	// Safe fallback if envs are missing.
	return process.env.NEXT_PUBLIC_ENV === "local"
		? "wss://api.devnet.solana.com"
		: "wss://api.mainnet-beta.solana.com";
}

/**
 * Get the base API URL (without /api suffix)
 * 
 * @returns The base URL for the application
 */
export function getBaseUrl(): string {
	if (typeof window !== "undefined") {
		const apiUrl = process.env.NEXT_PUBLIC_API_URL;
		
		if (apiUrl) {
			return apiUrl.replace('/api', '');
		}
		
		const protocol = window.location.protocol;
		const hostname = window.location.hostname;
		const port = window.location.port || '3999';
		
		return `${protocol}//${hostname}:${port}`;
	}
	
	return process.env.NEXT_PUBLIC_API_URL?.replace('/api', '') || 
		'http://localhost:3999';
}

/**
 * Get the API URL (with /api suffix)
 * 
 * @returns The API URL for the application
 */
export function getApiUrl(): string {
	if (typeof window !== "undefined") {
		const apiUrl = process.env.NEXT_PUBLIC_API_URL;
		
		if (apiUrl) {
			return apiUrl;
		}
		
		const protocol = window.location.protocol;
		const hostname = window.location.hostname;
		const port = window.location.port || '3999';
		
		return `${protocol}//${hostname}:${port}/api/`;
	}
	
	return process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3999/api/';
}
